import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
	useCreateUnlayerEmailTemplateMutation,
	useLazyGetUnlayerEmailTemplateDetailQuery,
	useLazyGetUnlayerEmailTemplateSummariesQuery,
} from '@/features/unlayerEmailTemplates/unlayerEmailTemplatesApi';
import { getUpdatedDesignWithSignature } from '@/components/UnlayerEditorWrapper/unlayerSignatureDesign';
import { showToast } from '@/features/toast/toastSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

const UnlayerTemplateContext = createContext(null);

/**
 * Encapsulates template list loading, template detail loading, and saving the
 * current compose state as a reusable template.
 */
export const UnlayerTemplateProvider = ({ children }) => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userBrokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [hasLoadedTemplateSummaries, setHasLoadedTemplateSummaries] = useState(false);
	const [isSaveTemplateDialogOpen, setIsSaveTemplateDialogOpen] = useState(false);
	const [templateNameInput, setTemplateNameInput] = useState('');
	const [templateNameError, setTemplateNameError] = useState('');
	const [templateActionError, setTemplateActionError] = useState('');
	const [createUnlayerEmailTemplate, { isLoading: isSavingTemplate }] =
		useCreateUnlayerEmailTemplateMutation();
	const [
		loadTemplateSummaries,
		{ data: templateSummaries = [], isFetching: isFetchingTemplates },
	] = useLazyGetUnlayerEmailTemplateSummariesQuery();
	const [loadTemplateDetail] = useLazyGetUnlayerEmailTemplateDetailQuery();

	const handleTemplateSelectorOpen = useCallback(() => {
		if (hasLoadedTemplateSummaries || userBrokerGroupIds.length === 0) {
			return;
		}
		setHasLoadedTemplateSummaries(true);
		loadTemplateSummaries({
			brokerGroupIds: userBrokerGroupIds,
		});
	}, [hasLoadedTemplateSummaries, loadTemplateSummaries, userBrokerGroupIds]);

	const handleTemplateSelect = useCallback(
		async (templateId) => {
			setSelectedTemplateId(templateId);
			if (!templateId) {
				return null;
			}

			try {
				return await loadTemplateDetail({ id: templateId }).unwrap();
			} catch (error) {
				console.log('🙅 ~ Error loading template detail', error);
				dispatch(
					showToast({
						message: 'Unable to load selected template.',
						error: true,
					}),
				);
				return null;
			}
		},
		[dispatch, loadTemplateDetail],
	);

	const openSaveTemplateDialog = useCallback((defaultName) => {
		setTemplateActionError('');
		setTemplateNameError('');
		setTemplateNameInput(defaultName);
		setIsSaveTemplateDialogOpen(true);
	}, []);

	const closeSaveTemplateDialog = useCallback(() => {
		setIsSaveTemplateDialogOpen(false);
		setTemplateNameError('');
	}, []);

	const updateTemplateNameInput = useCallback(
		(value) => {
			setTemplateNameInput(value);
			if (templateNameError) {
				setTemplateNameError('');
			}
		},
		[templateNameError],
	);

	const saveAsTemplate = useCallback(
		async ({ subject, editorDesign }) => {
			const normalizedName = templateNameInput.trim();
			if (!normalizedName) {
				setTemplateNameError('Template name is required.');
				return null;
			}
			try {
				setTemplateActionError('');
				setTemplateNameError('');
				const templateDesignWithoutSignature =
					editorDesign && typeof editorDesign === 'object'
						? getUpdatedDesignWithSignature(editorDesign, '')
						: {};
				const createdTemplate = await createUnlayerEmailTemplate({
					name: normalizedName,
					subject,
					designJson: templateDesignWithoutSignature,
					brokerGroupIds: userBrokerGroupIds,
				}).unwrap();
				await loadTemplateSummaries({
					brokerGroupIds: userBrokerGroupIds,
				});
				setSelectedTemplateId(createdTemplate?.id ?? '');
				setIsSaveTemplateDialogOpen(false);
				setTemplateNameInput('');
				dispatch(
					showToast({
						message: 'Template saved.',
						autohide: true,
					}),
				);
				return createdTemplate;
			} catch (error) {
				setTemplateActionError(error?.data?.message ?? 'Unable to save template.');
				return null;
			}
		},
		[createUnlayerEmailTemplate, dispatch, loadTemplateSummaries, templateNameInput, userBrokerGroupIds],
	);

	const clearSelectedTemplate = useCallback(() => {
		setSelectedTemplateId('');
	}, []);

	const value = useMemo(
		() => ({
			clearSelectedTemplate,
			handleTemplateSelect,
			handleTemplateSelectorOpen,
			hasLoadedTemplateSummaries,
			isFetchingTemplates,
			isSaveTemplateDialogOpen,
			isSavingTemplate,
			openSaveTemplateDialog,
			closeSaveTemplateDialog,
			saveAsTemplate,
			selectedTemplateId,
			templateActionError,
			templateNameError,
			templateNameInput,
			templateSummaries,
			updateTemplateNameInput,
		}),
		[
			clearSelectedTemplate,
			handleTemplateSelect,
			handleTemplateSelectorOpen,
			hasLoadedTemplateSummaries,
			isFetchingTemplates,
			isSaveTemplateDialogOpen,
			isSavingTemplate,
			openSaveTemplateDialog,
			closeSaveTemplateDialog,
			saveAsTemplate,
			selectedTemplateId,
			templateActionError,
			templateNameError,
			templateNameInput,
			templateSummaries,
			updateTemplateNameInput,
		],
	);

	return (
		<UnlayerTemplateContext.Provider value={value}>{children}</UnlayerTemplateContext.Provider>
	);
};

export const useUnlayerTemplateContext = () => {
	const context = useContext(UnlayerTemplateContext);
	if (!context) {
		throw new Error('useUnlayerTemplateContext must be used within an UnlayerTemplateProvider');
	}
	return context;
};
